/**
 * Spatial query markers: one instanced diamond per open or answered query in the room, at the
 * position where it was raised, or above the frame of its dashboard for queries without one
 * (seed queries, web-raised queries). Hover shows the query; select opens the card.
 */
import * as THREE from 'three';
import type { DashboardId, Query, QueryPriority, QueryStatus } from '@clinspace/shared';
import { PALETTE } from '../dashboards/charts.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';

export interface MarkerFrame {
  dashboard: DashboardId;
  /** Panel centre in world space and its yaw. */
  position: { x: number; y: number; z: number };
  yaw: number;
  width: number;
  height: number;
}

export interface MarkerLayout {
  id: string;
  position: [number, number, number];
  status: QueryStatus;
  priority: QueryPriority;
}

const MAX_MARKERS = 256;
const STEP = 0.11;

/** Pure placement so it can be unit tested. */
export function layoutMarkers(queries: Query[], frames: MarkerFrame[]): MarkerLayout[] {
  const perFrame = new Map<DashboardId, number>();
  const out: MarkerLayout[] = [];
  for (const q of queries) {
    if (q.status === 'Closed' || q.status === 'Cancelled') continue;
    if (out.length >= MAX_MARKERS) break;
    if (q.position) {
      out.push({
        id: q.id,
        position: [q.position.x, q.position.y, q.position.z],
        status: q.status,
        priority: q.priority,
      });
      continue;
    }
    const frame = frames.find((f) => f.dashboard === q.dashboard);
    if (!frame) continue;
    const i = perFrame.get(q.dashboard) ?? 0;
    perFrame.set(q.dashboard, i + 1);
    const lx = -frame.width / 2 + 0.1 + (i % 18) * STEP;
    const ly = frame.height / 2 + 0.14 + Math.floor(i / 18) * 0.12;
    const lz = 0.05;
    const cos = Math.cos(frame.yaw);
    const sin = Math.sin(frame.yaw);
    out.push({
      id: q.id,
      position: [
        frame.position.x + cos * lx + sin * lz,
        frame.position.y + ly,
        frame.position.z - sin * lx + cos * lz,
      ],
      status: q.status,
      priority: q.priority,
    });
  }
  return out;
}

export class QueryMarkers {
  readonly mesh: THREE.InstancedMesh;
  private ids: string[] = [];
  private hoveredIndex = -1;

  constructor(
    interactive: InteractiveSystem,
    private readonly handlers: {
      tooltip(id: string | undefined, at: THREE.Vector3 | undefined): void;
      select(id: string, at: THREE.Vector3): void;
    },
  ) {
    this.mesh = new THREE.InstancedMesh(
      new THREE.OctahedronGeometry(0.045, 0),
      new THREE.MeshStandardMaterial({
        roughness: 0.35,
        metalness: 0.1,
        emissive: 0xffffff,
        emissiveIntensity: 0.25,
      }),
      MAX_MARKERS,
    );
    this.mesh.name = 'query-markers';
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    interactive.add({
      object: this.mesh,
      onHover: (_p, point, hit) => {
        const i = hit?.instanceId ?? -1;
        if (i === this.hoveredIndex) return;
        this.hoveredIndex = i;
        handlers.tooltip(this.ids[i], point);
      },
      onLeave: () => {
        this.hoveredIndex = -1;
        handlers.tooltip(undefined, undefined);
      },
      onSelect: (_p, point, hit) => {
        const id = this.ids[hit?.instanceId ?? -1];
        if (id) handlers.select(id, point);
      },
    });
  }

  get count(): number {
    return this.ids.length;
  }

  set(queries: Query[], frames: MarkerFrame[]): void {
    const layout = layoutMarkers(queries, frames);
    const dummy = new THREE.Object3D();
    const colour = new THREE.Color();
    this.ids = layout.map((l) => l.id);
    layout.forEach((l, i) => {
      dummy.position.set(l.position[0], l.position[1], l.position[2]);
      dummy.rotation.set(0, (i * 0.7) % Math.PI, 0);
      dummy.scale.setScalar(l.priority === 'High' ? 1.3 : 1);
      dummy.updateMatrix();
      this.mesh.setMatrixAt(i, dummy.matrix);
      this.mesh.setColorAt(
        i,
        colour.set(
          l.status === 'Answered'
            ? PALETTE.teal
            : l.priority === 'High'
              ? PALETTE.crimson
              : PALETTE.amber,
        ),
      );
    });
    this.mesh.count = layout.length;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    // Rays are rejected outside the bounding sphere, which is only computed once by default.
    this.mesh.computeBoundingSphere();
  }

  positionOf(id: string, target = new THREE.Vector3()): THREE.Vector3 | undefined {
    const i = this.ids.indexOf(id);
    if (i < 0) return undefined;
    const m = new THREE.Matrix4();
    this.mesh.getMatrixAt(i, m);
    return target.setFromMatrixPosition(m);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
